import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChatMeetingChoice,
  ChatMeetingChoiceResponse,
  FollowupItem,
  FollowupSource,
  FollowupClearReason,
  GetFollowupItemsResponse,
  ClearFollowupItemResponse,
  UndoFollowupClearResponse,
  TriggerFollowupSweepResponse,
  SetFollowupSourceEnabledResponse,
  AgentEvents,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import api from '../../api';
import { ReactComponent as SlackLogo } from '../../icons/slack-logo.svg';
import { ReactComponent as LinkedInLogo } from '../../icons/linkedin.svg';
import { ReactComponent as FlipBackIcon } from '../../icons/flip-back.svg';
import granolaLogo from '../../icons/granola.png';
import './get-back-to-people-section.scss';

// ─── Source presentation ──────────────────────────────────────

const SOURCE_LABELS: Record<FollowupSource, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  slack: 'Slack',
  granola: 'Granola',
  linkedin: 'LinkedIn',
};

const ALL_SOURCES: FollowupSource[] = ['email', 'slack', 'granola'];
const CHAT_SOURCES: FollowupSource[] = ['whatsapp', 'linkedin'];

const SourceGlyph: React.FC<{ source: FollowupSource }> = ({ source }) => {
  switch (source) {
    case 'email':
      return (
        <svg viewBox='0 0 16 16' fill='none'>
          <rect
            x='1'
            y='3'
            width='14'
            height='10'
            rx='2'
            stroke='#EA4335'
            strokeWidth='1.5'
          />
          <path
            d='M1.6 4.2 8 8.9l6.4-4.7'
            stroke='#EA4335'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      );
    case 'whatsapp':
      return (
        <svg viewBox='0 0 16 16' fill='none'>
          <path
            d='M8 1.9a6 6 0 0 0-5.1 9.1L2.2 14l3.1-.8A6 6 0 1 0 8 1.9Z'
            stroke='#25D366'
            strokeWidth='1.5'
            strokeLinejoin='round'
          />
          <path
            d='M5.9 5.6c.5-.2.8.1 1 .5.3.7-.2.8.1 1.3.4.6 1 1.1 1.6 1.4.5.2.6-.3 1.2-.1.4.2.7.5.5 1-.2.5-.9.7-1.4.6-1.6-.3-3.3-2-3.6-3.6-.1-.5.1-1 .6-1.1Z'
            fill='#25D366'
          />
        </svg>
      );
    case 'slack':
      return <SlackLogo />;
    case 'linkedin':
      return <LinkedInLogo />;
    case 'granola':
      return <img src={granolaLogo} alt='' />;
    default:
      return null;
  }
};

// ─── Helpers ──────────────────────────────────────────────────

const formatWaiting = (lastInboundAt: string | null | undefined): string => {
  if (!lastInboundAt) return '';
  const ms = Date.now() - new Date(lastInboundAt).getTime();
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return 'Waiting under an hour';
  if (hours < 24) return `Waiting ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `Waiting ${days} day${days === 1 ? '' : 's'}`;
};

const formatAgo = (iso: string | null): string => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / (60 * 1000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const nextSweepLabel = (): string => {
  const now = new Date();
  const next = new Date(now);
  if (now.getHours() < 8) next.setHours(8, 0, 0, 0);
  else if (now.getHours() < 20) next.setHours(20, 0, 0, 0);
  else {
    next.setDate(next.getDate() + 1);
    next.setHours(8, 0, 0, 0);
  }
  return next
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '')
    .toLowerCase();
};

const sourceLabel = (item: FollowupItem): string => {
  const labels = [
    SOURCE_LABELS[item.source],
    ...item.secondarySources.map((s) => SOURCE_LABELS[s.source]),
  ];
  return labels.join(' + ');
};

const clearContract = (
  item: FollowupItem,
  reason: FollowupClearReason,
): string => {
  const firstName = item.personName.split(' ')[0];
  switch (reason) {
    case 'auto_replied':
      return 'You replied — cleared automatically';
    case 'replied':
    case 'no_reply':
      return `Won't return unless ${firstName} writes again`;
    case 'snooze':
      return 'Snoozed';
    case 'mute_thread':
      return 'Thread muted — never surfaces again';
    case 'never_person':
      return `Never chasing ${item.personName}, any channel`;
    case 'never_source':
      return `Stopped sweeping ${SOURCE_LABELS[item.source]}`;
    default:
      return 'Cleared';
  }
};

const clearedRowLabel = (item: FollowupItem): string => {
  const when = item.clearedAt ? formatAgo(item.clearedAt) : '';
  if (item.clearedAutomatically) return `Cleared automatically · ${when}`;
  switch (item.clearedReason) {
    case 'replied':
      return `I've replied · ${when}`;
    case 'no_reply':
      return `No reply needed · ${when}`;
    case 'snooze':
      return `Snoozed · ${when}`;
    case 'never_person':
      return `Never chase ${item.personName.split(' ')[0]} · ${when}`;
    case 'never_source':
      return `Source off · ${when}`;
    default:
      return when;
  }
};

const SNOOZE_OPTIONS: { label: string; days: number }[] = [
  { label: 'Tomorrow', days: 1 },
  { label: '3 days', days: 3 },
  { label: 'Next week', days: 7 },
];

interface Toast {
  itemId: string;
  description: string;
  contract: string;
}

// ─── Component ────────────────────────────────────────────────

interface GetBackToPeopleSectionProps {
  /** Report the open-item count so the host can build a subtitle. */
  onCountChange?: (count: number) => void;
  /**
   * Hand the sweep controls (status + "Sweep now") to the host so it can
   * place them in its own header; when absent they render in-section.
   */
  onActionsChange?: (actions: React.ReactNode | null) => void;
}

const GetBackToPeopleSection: React.FC<GetBackToPeopleSectionProps> = ({
  onCountChange,
  onActionsChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  const [openItems, setOpenItems] = useState<FollowupItem[]>([]);
  const [recentlyCleared, setRecentlyCleared] = useState<FollowupItem[]>([]);
  const [enabledSources, setEnabledSources] = useState<FollowupSource[]>([]);
  const [connectedSources, setConnectedSources] = useState<FollowupSource[]>(
    [],
  );
  const [togglingSource, setTogglingSource] = useState<FollowupSource | null>(
    null,
  );
  const [lastSweptAt, setLastSweptAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [sweepNotice, setSweepNotice] = useState<string | null>(null);
  const [clearedDrawerOpen, setClearedDrawerOpen] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [copiedDraftId, setCopiedDraftId] = useState<string | null>(null);
  const copiedDraftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [meetingActionId, setMeetingActionId] = useState<string | null>(null);
  const [meetingErrorId, setMeetingErrorId] = useState<string | null>(null);

  // Dropdown state — the row owning an open dropdown gets a raised z-index.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [snoozeOpenId, setSnoozeOpenId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const visibleSources = useMemo(
    () => [
      ...ALL_SOURCES,
      ...CHAT_SOURCES.filter((source) => connectedSources.includes(source)),
    ],
    [connectedSources],
  );

  const copyDraft = async (itemId: string, text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedDraftId(itemId);
      if (copiedDraftTimer.current) clearTimeout(copiedDraftTimer.current);
      copiedDraftTimer.current = setTimeout(() => setCopiedDraftId(null), 2000);
    } catch {
      setCopiedDraftId((current) => (current === itemId ? null : current));
    }
  };

  const fetchItems = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetFollowupItemsResponse>(
        '/followup-items',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOpenItems(response.data.openItems || []);
      setRecentlyCleared(response.data.recentlyCleared || []);
      setEnabledSources(response.data.enabledSources || []);
      setConnectedSources(response.data.connectedSources || []);
      setLastSweptAt(response.data.lastSweptAt || null);
    } catch (err) {
      console.error('Error fetching followup items:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(
    () => () => {
      if (copiedDraftTimer.current) clearTimeout(copiedDraftTimer.current);
    },
    [],
  );

  useEffect(() => {
    onCountChange?.(openItems.length);
  }, [openItems.length, onCountChange]);

  useEffect(() => {
    if (!userChannel) return;
    const handleSweepCompleted = () => {
      setSweeping(false);
      fetchItems();
    };
    userChannel.bind(
      AgentEvents.FOLLOWUP_SWEEP_COMPLETED,
      handleSweepCompleted,
    );
    return () => {
      userChannel.unbind(
        AgentEvents.FOLLOWUP_SWEEP_COMPLETED,
        handleSweepCompleted,
      );
    };
  }, [userChannel, fetchItems]);

  // Close the clear menu on outside click.
  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpenMenuId(null);
      setSnoozeOpenId(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuId]);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const showToast = useCallback((next: Toast) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);

  // ─── Actions ────────────────────────────────────────────────

  const handleClear = useCallback(
    async (
      item: FollowupItem,
      reason: FollowupClearReason,
      snoozeDays?: number,
    ) => {
      setOpenMenuId(null);
      setSnoozeOpenId(null);
      const contract = clearContract(item, reason);
      setOpenItems((prev) => prev.filter((i) => i.id !== item.id));
      if (reason === 'never_source') {
        setEnabledSources((prev) => prev.filter((s) => s !== item.source));
      }
      showToast({ itemId: item.id, description: item.description, contract });

      try {
        const token = await getAccessTokenSilently();
        const snoozeUntil =
          reason === 'snooze' && snoozeDays
            ? new Date(
                Date.now() + snoozeDays * 24 * 60 * 60 * 1000,
              ).toISOString()
            : undefined;
        const response = await api.post<ClearFollowupItemResponse>(
          `/followup-items/${item.id}/clear`,
          { reason, snoozeUntil },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const clearedItem = response.data.item;
        if (clearedItem) {
          setRecentlyCleared((prev) => [clearedItem, ...prev]);
        }
      } catch (err) {
        console.error('Error clearing followup item:', err);
        setToast(null);
        fetchItems();
      }
    },
    [getAccessTokenSilently, showToast, fetchItems],
  );

  const handleMeetingChoice = useCallback(
    async (item: FollowupItem, choice: ChatMeetingChoice) => {
      if (meetingActionId) return;
      setMeetingActionId(item.id);
      setMeetingErrorId(null);
      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<ChatMeetingChoiceResponse>(
          `/followup-items/${item.id}/chat-meeting`,
          { choice },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.data.success) throw new Error(response.data.error);
        if (choice === 'ignore') {
          setOpenItems((prev) =>
            prev.filter((openItem) => openItem.id !== item.id),
          );
          return;
        }
        if (!response.data.chatDraft) throw new Error('Missing chat draft');
        setOpenItems((prev) =>
          prev.map((openItem) =>
            openItem.id === item.id
              ? { ...openItem, chatDraft: response.data.chatDraft }
              : openItem,
          ),
        );
      } catch {
        setMeetingErrorId(item.id);
      } finally {
        setMeetingActionId(null);
      }
    },
    [getAccessTokenSilently, meetingActionId],
  );

  const handleUndo = useCallback(
    async (itemId: string) => {
      setToast(null);
      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<UndoFollowupClearResponse>(
          `/followup-items/${itemId}/undo`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const reopenedItem = response.data.item;
        if (reopenedItem) {
          setRecentlyCleared((prev) => prev.filter((i) => i.id !== itemId));
          setOpenItems((prev) => [reopenedItem, ...prev]);
        }
        fetchItems();
      } catch (err) {
        console.error('Error undoing followup clear:', err);
        fetchItems();
      }
    },
    [getAccessTokenSilently, fetchItems],
  );

  const handleSweepNow = useCallback(async () => {
    setSweepNotice(null);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<TriggerFollowupSweepResponse>(
        '/followup-items/sweep',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.started) {
        setSweeping(true);
      } else if (response.data.error) {
        setSweepNotice(response.data.error);
      }
    } catch (err) {
      console.error('Error starting sweep:', err);
    }
  }, [getAccessTokenSilently]);

  const openManageSources = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  const handleToggleSource = useCallback(
    async (source: FollowupSource, enabled: boolean) => {
      setTogglingSource(source);
      // Optimistic flip; the response is authoritative.
      setEnabledSources((prev) =>
        enabled ? [...prev, source] : prev.filter((s) => s !== source),
      );
      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<SetFollowupSourceEnabledResponse>(
          `/followup-items/sources/${source}`,
          { enabled },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setEnabledSources(response.data.enabledSources || []);
        setConnectedSources(response.data.connectedSources || []);
      } catch (err) {
        console.error('Error toggling followup source:', err);
        fetchItems();
      } finally {
        setTogglingSource(null);
      }
    },
    [getAccessTokenSilently, fetchItems],
  );

  const sortedOpenItems = useMemo(() => {
    // Resurfaced items first, then by how long the person has been waiting.
    return [...openItems].sort((a, b) => {
      const aRes = a.resurfaceReason ? 0 : 1;
      const bRes = b.resurfaceReason ? 0 : 1;
      if (aRes !== bRes) return aRes - bRes;
      const aTime = a.lastInboundAt ? new Date(a.lastInboundAt).getTime() : 0;
      const bTime = b.lastInboundAt ? new Date(b.lastInboundAt).getTime() : 0;
      return aTime - bTime;
    });
  }, [openItems]);

  const sweepControls = useMemo(
    () => (
      <div className='gbp-sweep-controls'>
        <button
          className='gbp-sweep-btn'
          onClick={handleSweepNow}
          disabled={sweeping}
        >
          <svg viewBox='0 0 20 20' fill='none'>
            <path
              d='M17.5 10a7.5 7.5 0 1 1-2.2-5.3M17.5 2.5v4.2h-4.2'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <span>{sweeping ? 'Sweeping…' : 'Sweep now'}</span>
        </button>
        <span className='gbp-sweep-status'>
          {lastSweptAt ? `Swept ${formatAgo(lastSweptAt)}` : 'Not swept yet'}
          <span className='gbp-sep' />
          next sweep {nextSweepLabel()}
        </span>
      </div>
    ),
    [lastSweptAt, sweeping, handleSweepNow],
  );

  useEffect(() => {
    if (!onActionsChange) return;
    onActionsChange(loading ? null : sweepControls);
    return () => onActionsChange(null);
  }, [onActionsChange, loading, sweepControls]);

  if (loading) return null;

  return (
    <div className='gbp-section'>
      {!onActionsChange && sweepControls}

      <div className='gbp-sources'>
        <div className='gbp-sources-head'>
          <span className='gbp-sources-label'>Sweeping</span>
          <span className='gbp-sources-hint'>
            Where Dream looks for replies you owe
          </span>
        </div>
        <div className='gbp-sources-list'>
          {visibleSources.map((source) => {
            const connected = connectedSources.includes(source);
            const enabled = enabledSources.includes(source);
            if (!connected) {
              return (
                <button
                  key={source}
                  className='gbp-source-chip not-connected'
                  title={`Connect ${SOURCE_LABELS[source]} in Settings`}
                  onClick={openManageSources}
                >
                  <SourceGlyph source={source} />
                  <span>{SOURCE_LABELS[source]}</span>
                  <span className='gbp-source-connect'>Connect</span>
                </button>
              );
            }
            return (
              <button
                key={source}
                className={`gbp-source-chip${enabled ? ' enabled' : ''}`}
                disabled={togglingSource === source}
                aria-pressed={enabled}
                title={
                  enabled
                    ? `Stop sweeping ${SOURCE_LABELS[source]}`
                    : `Sweep ${SOURCE_LABELS[source]}`
                }
                onClick={() => handleToggleSource(source, !enabled)}
              >
                <SourceGlyph source={source} />
                <span>{SOURCE_LABELS[source]}</span>
                <span className={`gbp-source-switch${enabled ? ' on' : ''}`}>
                  <span className='gbp-source-knob' />
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {sweepNotice && <div className='gbp-sweep-notice'>{sweepNotice}</div>}

      <div className='gbp-list'>
        {sortedOpenItems.length === 0 && (
          <div className='gbp-empty'>
            <span className='gbp-empty-icon' aria-hidden='true'>
              <FlipBackIcon />
            </span>
            <h3>All caught up</h3>
            <p>Nobody's waiting on you. The sweep runs twice a day.</p>
          </div>
        )}

        {sortedOpenItems.map((item) => {
          const menuOpen = openMenuId === item.id;
          const firstName = item.personName.split(' ')[0];
          const chatDraft = item.chatDraft;
          return (
            <div
              key={item.id}
              className={`gbp-item${item.resurfaceReason ? ' has-panel' : ''}${menuOpen ? ' menu-open' : ''}`}
            >
              <button
                className='gbp-circle'
                aria-label="I've replied — clear"
                onClick={() => handleClear(item, 'replied')}
              >
                <svg viewBox='0 0 24 24' fill='none'>
                  <circle
                    cx='12'
                    cy='12'
                    r='11.5'
                    fill='var(--dream-bg-surface-raised)'
                    stroke='currentColor'
                  />
                </svg>
              </button>

              <div className='gbp-mid'>
                <p className='gbp-desc'>{item.description}</p>
                {item.detail && <p className='gbp-detail'>{item.detail}</p>}
                {chatDraft && chatDraft.meetingDecision === 'pending' ? (
                  <div
                    className='gbp-chat-meeting'
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <p>{t('chatMeeting.prompt', { name: firstName })}</p>
                    <div className='gbp-chat-meeting-actions'>
                      <button
                        type='button'
                        disabled={meetingActionId !== null}
                        onClick={() => void handleMeetingChoice(item, 'meet')}
                      >
                        {t('chatMeeting.yes')}
                      </button>
                      <button
                        type='button'
                        disabled={meetingActionId !== null}
                        onClick={() =>
                          void handleMeetingChoice(item, 'decline')
                        }
                      >
                        {t('chatMeeting.decline')}
                      </button>
                      <button
                        type='button'
                        disabled={meetingActionId !== null}
                        onClick={() => void handleMeetingChoice(item, 'ignore')}
                      >
                        {t('chatMeeting.notInterested')}
                      </button>
                      <a
                        href={chatDraft.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={(event) => event.stopPropagation()}
                      >
                        {item.source === 'linkedin'
                          ? 'Open in LinkedIn'
                          : 'Open WhatsApp'}
                      </a>
                    </div>
                    {meetingErrorId === item.id && (
                      <p className='gbp-chat-meeting-error' role='alert'>
                        {t('chatMeeting.error')}
                      </p>
                    )}
                  </div>
                ) : (
                  chatDraft && (
                    <div
                      className='gbp-chat-draft'
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <blockquote className='gbp-chat-draft-text'>
                        {chatDraft.text}
                      </blockquote>
                      <div className='gbp-chat-draft-actions'>
                        <button
                          type='button'
                          onClick={(event) => {
                            event.stopPropagation();
                            void copyDraft(item.id, chatDraft.text);
                          }}
                        >
                          {copiedDraftId === item.id ? 'Copied' : 'Copy draft'}
                        </button>
                        <a
                          href={chatDraft.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          onClick={(event) => event.stopPropagation()}
                        >
                          {item.source === 'linkedin'
                            ? 'Open in LinkedIn'
                            : 'Open WhatsApp'}
                        </a>
                      </div>
                      {item.source === 'whatsapp' &&
                        chatDraft.delivery === 'in_app' && (
                          <p className='gbp-chat-draft-note'>
                            Also in WhatsApp on your Mac
                          </p>
                        )}
                    </div>
                  )
                )}
                <div className='gbp-meta'>
                  {item.resurfaceReason && (
                    <span
                      className={`gbp-resurfaced${item.resurfacedAs === 'snooze' ? ' is-snooze' : ''}`}
                    >
                      <svg viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M14 8A6 6 0 1 1 8 2c2.2 0 4 1.2 5 3'
                          stroke='currentColor'
                          strokeWidth='1.6'
                          strokeLinecap='round'
                        />
                        <path
                          d='M13.4 2v3.2h-3.2'
                          stroke='currentColor'
                          strokeWidth='1.6'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      {item.resurfacedAs === 'snooze' ? 'Snooze ended' : 'Back'}
                    </span>
                  )}
                  <span className='gbp-src-name'>
                    <span className='gbp-src-glyphs'>
                      <SourceGlyph source={item.source} />
                      {item.secondarySources.map((s) => (
                        <SourceGlyph key={s.source} source={s.source} />
                      ))}
                    </span>
                    {sourceLabel(item)}
                  </span>
                  {item.lastInboundAt && (
                    <span className='gbp-dot gbp-waiting'>
                      {formatWaiting(item.lastInboundAt)}
                    </span>
                  )}
                </div>
                {item.resurfaceReason && (
                  <div className='gbp-why-back'>
                    <b>What&apos;s new:</b> {item.resurfaceReason}
                  </div>
                )}
              </div>

              <div className='gbp-right'>
                <button
                  className='gbp-dots-btn'
                  aria-label='More options'
                  onClick={(e) => {
                    e.stopPropagation();
                    setSnoozeOpenId(null);
                    setOpenMenuId(menuOpen ? null : item.id);
                  }}
                >
                  <svg viewBox='0 0 16 16' width='16' height='16'>
                    <circle cx='8' cy='3.2' r='1.4' fill='currentColor' />
                    <circle cx='8' cy='8' r='1.4' fill='currentColor' />
                    <circle cx='8' cy='12.8' r='1.4' fill='currentColor' />
                  </svg>
                </button>
                {menuOpen && (
                  <div className='gbp-menu' ref={menuRef}>
                    <div className='gbp-menu-label'>Clear it</div>
                    <div
                      className='gbp-menu-item'
                      onClick={() => handleClear(item, 'replied')}
                    >
                      <span>
                        I&apos;ve replied
                        <span className='sub'>
                          Back only if {firstName} writes again
                        </span>
                      </span>
                    </div>
                    <div
                      className='gbp-menu-item'
                      onClick={() => handleClear(item, 'no_reply')}
                    >
                      <span>
                        No reply needed
                        <span className='sub'>
                          Back only if {firstName} writes again
                        </span>
                      </span>
                    </div>
                    <div
                      className='gbp-menu-item has-submenu'
                      onClick={() =>
                        setSnoozeOpenId(
                          snoozeOpenId === item.id ? null : item.id,
                        )
                      }
                    >
                      <span>
                        Snooze
                        <span className='sub'>
                          Tomorrow · 3 days · next week
                        </span>
                      </span>
                    </div>
                    {snoozeOpenId === item.id && (
                      <div className='gbp-snooze-options'>
                        {SNOOZE_OPTIONS.map((opt) => (
                          <button
                            key={opt.days}
                            className='gbp-snooze-option'
                            onClick={() =>
                              handleClear(item, 'snooze', opt.days)
                            }
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className='gbp-menu-sep' />
                    <div className='gbp-menu-label'>Stop asking</div>
                    <div
                      className='gbp-menu-item'
                      onClick={() => handleClear(item, 'mute_thread')}
                    >
                      <span>
                        Mute this thread
                        <span className='sub'>
                          Never surfaces again, whatever arrives
                        </span>
                      </span>
                    </div>
                    <div
                      className='gbp-menu-item'
                      onClick={() => handleClear(item, 'never_person')}
                    >
                      <span>
                        Never chase {item.personName}
                        <span className='sub'>Any channel, any message</span>
                      </span>
                    </div>
                    <div
                      className='gbp-menu-item'
                      onClick={() => handleClear(item, 'never_source')}
                    >
                      <span>
                        Stop sweeping {SOURCE_LABELS[item.source]}
                        <span className='sub'>Turns the whole source off</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {recentlyCleared.length > 0 && (
        <div className='gbp-cleared-drawer'>
          <button
            className='gbp-cleared-toggle'
            onClick={() => setClearedDrawerOpen((v) => !v)}
          >
            <span className='gbp-cleared-title'>Recently cleared</span>
            <span className='gbp-cleared-count'>{recentlyCleared.length}</span>
            <span className='gbp-cleared-chevron'>
              {clearedDrawerOpen ? 'Hide' : 'Show'}
            </span>
          </button>
          {clearedDrawerOpen && (
            <div className='gbp-cleared-list'>
              {recentlyCleared.map((item) => (
                <div key={item.id} className='gbp-cleared-row'>
                  <span
                    className='gbp-src-tile small'
                    title={SOURCE_LABELS[item.source]}
                  >
                    <SourceGlyph source={item.source} />
                  </span>
                  <span className='gbp-struck'>{item.description}</span>
                  {item.clearedReason === 'mute_thread' ? (
                    <span className='gbp-mute-tag'>Thread muted</span>
                  ) : (
                    <span className='gbp-cleared-when'>
                      {clearedRowLabel(item)}
                    </span>
                  )}
                  <button
                    className='gbp-undo-btn'
                    onClick={() => handleUndo(item.id)}
                  >
                    {item.clearedReason === 'mute_thread'
                      ? 'Unmute'
                      : 'Bring back'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {toast && (
        <div className='gbp-toast-host'>
          <div className='gbp-toast'>
            <span className='gbp-struck'>{toast.description}</span>
            <span className='gbp-toast-contract'>{toast.contract}</span>
            <button
              className='gbp-undo-btn'
              onClick={() => handleUndo(toast.itemId)}
            >
              Undo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetBackToPeopleSection;
